import { RouterLink } from '@angular/router';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Permissions } from '@app/core/guard/Permissions';
import {
  BankLimitItemResponse,
  BankLimitsResponse,
} from '@interfaces/bank-limits/responses/bank-limits.response';
import { RfqChanges } from '@interfaces/realtime/events';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { BankLimitsHttpService } from '@services/http/bank-limits/bank-limits-http.service';
import { BankLimitUpdateHttpService } from '@services/http/bank-limits/bank-limit-update-http.service';
import {
  BANK_LIMIT_REQUEST_FAILED,
  bankLimitRequestSentMessage,
  bankLimitUpdateErrorMessage,
} from '@services/data/bank-limits/bank-limit-update-error.util';
import { RealtimeService } from '@services/realtime/realtime.service';
import { ToastHelperService } from '@src/app/core/services/helpers/toast-helper.service';
import { SharedModule } from '@shared/shared.module';
import { rfqChanged$ } from '@features/dashboard/control/risk-management/monitoring/pages/rfqs/helpers/rfq-realtime-refresh';
import { ModalBankLimitProposalComponent } from '@features/dashboard/market/banking-relationship/components/modal-bank-limit-proposal/modal-bank-limit-proposal.component';
import { catchError, finalize, map, of, Subject, switchMap, tap } from 'rxjs';
import {
  BankLimitProposalBadgeView,
  bankLimitProposalBadge,
  BankLimitRequestView,
  bankLimitRequestView,
  BankLimitUpdatePermissions,
  bankInitials,
  bankLimitStatusText,
  BankLimitStatusText,
  formatCalendarDate,
  formatCompactMillions,
  NotInformedGroupAction,
  notInformedGroupAction,
  notInformedGroupDetail,
  notInformedHeadline,
  partitionBankLimits,
  stackedLogos,
  usagePercent,
  usageTone,
  UsageTone,
} from './bank-limits-display.util';
import { HomeBankAvatarComponent } from './home-bank-avatar/home-bank-avatar.component';
import { HomeBankLimitUpdateActionsComponent } from './home-bank-limit-update-actions/home-bank-limit-update-actions.component';
import { HomeBankLimitsUpdateFooterComponent } from './home-bank-limits-update-footer/home-bank-limits-update-footer.component';

/** Leitura do card: `silent` = relida por evento/pedido, sem voltar ao esqueleto se já há dados. */
interface LoadRequest {
  companyId: string;
  silent: boolean;
}

/** Mudanças do `rfq.changed` que alteram o pedido ou a resposta de limites (card 3787). */
const LIMIT_UPDATE_CHANGES: readonly string[] = [
  RfqChanges.Sent,
  RfqChanges.EmailReceived,
  RfqChanges.LimitProposalCreated,
  RfqChanges.LimitProposalConfirmed,
  RfqChanges.LimitProposalRejected,
];

type LoadResult = { ok: true; data: BankLimitsResponse | null } | { ok: false };

/** Linha pronta para o template: o DTO do banco mais os textos de exibição. */
interface BankLimitRow {
  id: string;
  bankName: string;
  bankCode: string | null;
  initials: string;
  isOk: boolean;
  /** Ok só com derivativos vigentes: não há limite de 4131 para mostrar a barra. */
  has4131: boolean;
  available: number | null;
  validityEnd: string;
  usedLabel: string;
  totalLabel: string;
  derivativesLabel: string;
  usagePct: number;
  usageTone: UsageTone;
  lastUpdatedAt: string | null;
  warning: BankLimitStatusText | null;
  financialInstitutionId: string;
  request: BankLimitRequestView | null;
  proposal: BankLimitProposalBadgeView | null;
}

/** Banco sem limite informado, dentro do grupo compacto do fim da lista. */
interface NotInformedBank {
  id: string;
  bankName: string;
  bankCode: string | null;
  initials: string;
  financialInstitutionId: string;
  request: BankLimitRequestView | null;
  proposal: BankLimitProposalBadgeView | null;
}

/** Linha única que junta todos os bancos NotInformed (logos empilhados + lista ao clicar em Ver). */
interface NotInformedGroup {
  headline: string;
  detail: string;
  banks: NotInformedBank[];
  visibleLogos: NotInformedBank[];
  hiddenCount: number;
  /** Uma ação para o grupo todo (crítica G7), em vez de um botão por banco. */
  action: NotInformedGroupAction | null;
}

/** Chave do pedido em grupo no `requestingIds`. */
const GROUP_REQUEST_KEY = '__not-informed-group__';

/**
 * Card "Limites Bancários" da Home (card 3786, protótipo `nova-dashboard-com-cog-e-limites`):
 * total disponível de 4131 e a situação de cada banco com relacionamento da empresa.
 * Dados: `GET dashboard/bank-limits?companyId=`, refeito quando a empresa muda ou no Atualizar do
 * cabeçalho. Todos os números chegam prontos do backend; aqui só se formata.
 * Sem empresa única escolhida (`companyId` nulo), mostra o pedido para escolher uma.
 *
 * Pedido de atualização de limites (card 3787): botão "Pedir atualização ao banco" nas linhas
 * vencidas e sem limite, selo da resposta do banco aguardando conferência (abre a modal de
 * confirmação) e o rodapé "Próximo pedido aos bancos". O `rfq.changed` relê o card em silêncio.
 * Regras de exibição em `bank-limits-display.util.ts`.
 */
@Component({
  selector: 'app-home-bank-limits-card',
  standalone: true,
  imports: [
    SharedModule,
    RouterLink,
    HomeBankAvatarComponent,
    HomeBankLimitUpdateActionsComponent,
    HomeBankLimitsUpdateFooterComponent,
    ModalBankLimitProposalComponent,
  ],
  templateUrl: './home-bank-limits-card.component.html',
  styleUrls: ['./home-bank-limits-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeBankLimitsCardComponent {
  private readonly http = inject(BankLimitsHttpService);
  private readonly limitUpdateHttp = inject(BankLimitUpdateHttpService);
  private readonly dateFormatter = inject(DateFormatterService);
  private readonly toast = inject(ToastHelperService);
  private readonly permissionsService = inject(PermissionsService);

  @ViewChild('proposalModal') proposalModal?: ModalBankLimitProposalComponent;

  private readonly canRequestLimitUpdate = toSignal(
    this.permissionsService.hasPermission(Permissions.BankRelationship.REQUEST_LIMIT_UPDATE),
    { initialValue: false }
  );
  private readonly canConfirmLimitUpdate = toSignal(
    this.permissionsService.hasPermission(Permissions.BankRelationship.CONFIRM_LIMIT_UPDATE),
    { initialValue: false }
  );
  private readonly limitPermissions = computed<BankLimitUpdatePermissions>(() => ({
    canRequest: this.canRequestLimitUpdate(),
    canConfirm: this.canConfirmLimitUpdate(),
  }));
  /** Bancos (`financialInstitutionId`) com pedido em andamento: spinner no botão. */
  readonly requestingIds = signal<ReadonlySet<string>>(new Set());

  /** Empresa escolhida no filtro da Home; `null` com nenhuma ou várias empresas. */
  readonly companyId = input<string | null>(null);
  /** Muda quando a pessoa clica em Atualizar no cabeçalho da Home. */
  readonly refreshKey = input(0);

  readonly loading = signal(false);
  readonly failed = signal(false);
  readonly limits = signal<BankLimitsResponse | null>(null);

  readonly relationshipsRoute = ['/dashboard/market/banking-relationship/list'];

  private readonly partition = computed(() => partitionBankLimits(this.limits()?.banks ?? []));

  /** Ok, Expired e o NotInformed com resposta aguardando conferência (G1), uma linha cada. */
  readonly rows = computed<BankLimitRow[]>(() => {
    const permissions = this.limitPermissions();
    return this.partition().listed.map(bank => this.toRow(bank, permissions));
  });

  /** Todos os NotInformed numa linha só no fim (evita 13 linhas iguais); `null` se não há nenhum. */
  readonly notInformedGroup = computed<NotInformedGroup | null>(() => {
    const permissions = this.limitPermissions();
    const source = this.partition().notInformed;
    const banks = source.map(bank => {
      const views = this.limitUpdateViews(bank, permissions);
      return {
        id: bank.bankRelationshipId,
        bankName: bank.bankName,
        bankCode: bank.bankCode,
        initials: bankInitials(bank.bankName),
        ...views,
        // Por banco, só o "Pedido enviado"; botão e aviso de contato ficam no grupo (crítica G7).
        request: views.request?.kind === 'sent' ? views.request : null,
      };
    });
    if (banks.length === 0) {
      return null;
    }
    const { visible, hiddenCount } = stackedLogos(banks);
    return {
      headline: notInformedHeadline(banks.length),
      detail: notInformedGroupDetail(source, permissions, sentAt =>
        this.dateFormatter.formatDateTimeInFormat(sentAt, 'dd/MM')
      ),
      banks,
      visibleLogos: visible,
      hiddenCount,
      action: notInformedGroupAction(source, permissions),
    };
  });

  /** Há relacionamento, mas nenhum banco com limite (todos NotInformed). */
  readonly allNotInformed = computed(() => {
    // Pelo status, não pelas linhas: o NotInformed com resposta pendente ganha linha própria (G1).
    const banks = this.limits()?.banks ?? [];
    return banks.length > 0 && banks.every(bank => bank.status === 'NotInformed');
  });
  readonly hasBanks = computed(() => (this.limits()?.banks?.length ?? 0) > 0);
  readonly notInformedExpanded = signal(false);

  readonly totalAvailable = computed(() => this.limits()?.totalAvailable4131 ?? 0);

  readonly schedule = computed(() => this.limits()?.limitUpdate ?? null);

  private readonly requests$ = new Subject<LoadRequest>();

  constructor() {
    this.requests$
      .pipe(
        tap(({ silent }) => {
          // Releitura por evento ou depois do pedido: com dados na tela, troca sem piscar.
          if (!silent || !this.limits()) {
            this.loading.set(true);
          }
          this.failed.set(false);
        }),
        switchMap(({ companyId }) =>
          this.http.getByCompany(companyId).pipe(
            map((data): LoadResult => ({ ok: true, data })),
            catchError(() => of<LoadResult>({ ok: false }))
          )
        ),
        takeUntilDestroyed(inject(DestroyRef))
      )
      .subscribe(result => {
        this.loading.set(false);
        if (!result.ok || !result.data) {
          this.failed.set(true);
          this.limits.set(null);
          return;
        }
        const sameCompany = this.limits()?.companyId === result.data.companyId;
        this.limits.set(result.data);
        if (!sameCompany) {
          this.notInformedExpanded.set(false);
        }
      });

    // Pedido enviado, resposta do banco e proposta decidida (card 3787): o evento é só a dica.
    rfqChanged$(inject(RealtimeService), payload => LIMIT_UPDATE_CHANGES.includes(payload.change))
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe(() => this.reload(true));

    effect(() => {
      const companyId = this.companyId();
      this.refreshKey();
      untracked(() => {
        if (companyId) {
          this.requests$.next({ companyId, silent: false });
          return;
        }
        this.limits.set(null);
        this.failed.set(false);
        this.loading.set(false);
      });
    });
  }

  retry(): void {
    this.reload(false);
  }

  /** Relê o card; `silent` mantém os dados na tela enquanto lê. */
  reload(silent: boolean): void {
    const companyId = this.companyId();
    if (companyId) {
      this.requests$.next({ companyId, silent });
    }
  }

  /** "Pedir atualização ao banco" de uma linha: cria e envia o pedido na hora. */
  requestUpdate(bank: { financialInstitutionId: string }): void {
    this.sendRequest([bank.financialInstitutionId], bank.financialInstitutionId);
  }

  /** "Pedir limites aos N bancos" do grupo sem limite: um pedido só para todos. */
  requestGroup(financialInstitutionIds: string[]): void {
    this.sendRequest(financialInstitutionIds, GROUP_REQUEST_KEY);
  }

  isGroupRequesting(): boolean {
    return this.requestingIds().has(GROUP_REQUEST_KEY);
  }

  private sendRequest(financialInstitutionIds: string[], busyKey: string): void {
    const companyId = this.companyId();
    if (!companyId || !financialInstitutionIds.length || this.requestingIds().has(busyKey)) {
      return;
    }

    this.setRequesting(busyKey, true);
    this.limitUpdateHttp
      .request({ companyId, financialInstitutionIds })
      .pipe(finalize(() => this.setRequesting(busyKey, false)))
      .subscribe({
        next: result => {
          this.toast.showToastSuccess(
            'Pedido enviado',
            bankLimitRequestSentMessage((result?.sentBanks ?? []).map(bank => bank.bankName))
          );
          this.reload(true);
        },
        error: error =>
          this.toast.showToastWarn(
            'Pedido não enviado',
            bankLimitUpdateErrorMessage(error, BANK_LIMIT_REQUEST_FAILED)
          ),
      });
  }

  openProposal(proposalId: string): void {
    this.proposalModal?.open(proposalId);
  }

  toggleNotInformed(): void {
    this.notInformedExpanded.update(expanded => !expanded);
  }

  private setRequesting(key: string, busy: boolean): void {
    this.requestingIds.update(current => {
      const next = new Set(current);
      if (busy) next.add(key);
      else next.delete(key);
      return next;
    });
  }

  /** Botão/texto do pedido e selo da resposta de um banco (regras no util). */
  private limitUpdateViews(
    bank: BankLimitItemResponse,
    permissions: BankLimitUpdatePermissions
  ): Pick<BankLimitRow, 'financialInstitutionId' | 'request' | 'proposal'> {
    return {
      financialInstitutionId: bank.financialInstitutionId,
      request: bankLimitRequestView(bank.status, bank.limitUpdate, permissions, sentAt =>
        this.dateFormatter.formatDateTimeInFormat(sentAt, 'dd/MM')
      ),
      proposal: bankLimitProposalBadge(bank.limitUpdate, permissions),
    };
  }

  private toRow(
    bank: BankLimitItemResponse,
    permissions: BankLimitUpdatePermissions
  ): BankLimitRow {
    return {
      id: bank.bankRelationshipId,
      bankName: bank.bankName,
      bankCode: bank.bankCode,
      initials: bankInitials(bank.bankName),
      isOk: bank.status === 'Ok',
      has4131: bank.limit4131Total !== null && bank.limit4131Total !== undefined,
      available: bank.limit4131Available,
      validityEnd: formatCalendarDate(bank.validityEndDate),
      usedLabel: formatCompactMillions(bank.limit4131Used),
      totalLabel: formatCompactMillions(bank.limit4131Total),
      derivativesLabel: formatCompactMillions(bank.derivativesFree),
      usagePct: usagePercent(bank.limit4131Used, bank.limit4131Total),
      usageTone: usageTone(usagePercent(bank.limit4131Used, bank.limit4131Total)),
      lastUpdatedAt: bank.lastUpdatedAt,
      warning: bankLimitStatusText(bank.status, bank.validityEndDate, bank.limit4131Total),
      ...this.limitUpdateViews(bank, permissions),
    };
  }
}
